import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  label: string;
  icon: string;
  faqs: FAQ[];
}

const faqCategories: FAQCategory[] = [
  {
    id: 'general',
    label: 'General',
    icon: 'ri-question-line',
    faqs: [
      {
        id: 'what-is-life-insurance',
        question: 'What is life insurance and why do I need it?',
        answer:
          'Life insurance is a contract between you and an insurance company. In exchange for regular premium payments, the insurer pays a tax-free lump sum (the "death benefit") to your chosen beneficiaries when you pass away. It ensures your family can cover expenses like mortgage payments, childcare, college tuition, and daily living costs — even if you\'re no longer there to provide for them. We offer several types of coverage including <a href="/term-life" class="text-[#D97642] font-semibold hover:underline">term life</a>, <a href="/whole-life" class="text-[#D97642] font-semibold hover:underline">whole life</a>, and <a href="/final-expense" class="text-[#D97642] font-semibold hover:underline">final expense insurance</a>.',
      },
      {
        id: 'how-much-coverage',
        question: 'How much life insurance coverage do I need?',
        answer:
          'A common starting point is 10–12 times your annual income, plus any outstanding debts (mortgage, car loans, credit cards). For example, if you earn $70,000/year with a $200,000 mortgage, consider $900,000–$1,040,000 in coverage. Your actual needs depend on your family size, income replacement goals, future expenses like college, and existing savings. A free consultation with Benjamin Wright can help you calculate the right number. <a href="/book-appointment" class="text-[#D97642] font-semibold hover:underline">Book your free consultation here →</a>',
      },
      {
        id: 'term-vs-whole',
        question: 'What is the difference between term and whole life insurance?',
        answer:
          '<a href="/term-life" class="text-[#D97642] font-semibold hover:underline">Term life insurance</a> provides coverage for a set period (10, 20, or 30 years) at a fixed, affordable premium. If you pass away during the term, your beneficiaries receive the death benefit. <a href="/whole-life" class="text-[#D97642] font-semibold hover:underline">Whole life insurance</a> is permanent — it covers you for life, builds cash value over time, and premiums never increase. Term is best for income replacement during working years; whole life is ideal for estate planning, legacy building, or permanent protection. See our full <a href="/term-vs-whole-life" class="text-[#D97642] font-semibold hover:underline">Term vs. Whole Life comparison guide →</a>',
      },
      {
        id: 'indiana-specific',
        question: 'Are there Indiana-specific rules I should know about?',
        answer:
          'Yes. Indiana residents benefit from strong consumer protections: (1) A minimum 10-day free look period (most carriers offer 30 days) — cancel for any reason and get a full refund. (2) The Indiana Life & Health Guaranty Association protects your policy up to $300,000 if your carrier becomes insolvent. (3) Indiana does not tax life insurance death benefits, and there is no state estate tax. All carriers must be licensed by the Indiana Department of Insurance (in.gov/idoi). Learn more on our <a href="/life-insurance-indiana" class="text-[#D97642] font-semibold hover:underline">Indiana life insurance guide →</a>',
      },
      {
        id: 'independent-agent',
        question: 'What is an independent insurance agent and why does it matter?',
        answer:
          'An independent agent like Benjamin Wright is not tied to a single insurance company. He can compare rates and policies from 20+ top-rated carriers (Mutual of Omaha, Prudential, Transamerica, and more) to find the best fit for your specific age, health, and budget. A captive agent (like those at State Farm or Allstate) can only offer their company\'s products — which may not be the best option for you. <a href="/about" class="text-[#D97642] font-semibold hover:underline">Learn more about Benjamin →</a>',
      },
    ],
  },
  {
    id: 'term-life',
    label: 'Term Life',
    icon: 'ri-calendar-check-line',
    faqs: [
      {
        id: 'term-life-cost-indiana',
        question: 'How much does term life insurance cost in Indiana?',
        answer:
          'A healthy 35-year-old non-smoker in Indiana can get $500,000 of 20-year term coverage for approximately $25–$40/month. Rates vary based on age, health, tobacco use, term length, and coverage amount. Indiana residents typically pay 8–12% less than the national average due to the state\'s lower cost of living and competitive insurance market. <a href="/term-life" class="text-[#D97642] font-semibold hover:underline">Get your personalized term life quote →</a>',
      },
      {
        id: 'term-length',
        question: 'What term length should I choose — 10, 20, or 30 years?',
        answer:
          'Choose a term that covers your biggest financial obligations. A 20-year term is the most popular — it covers the years when your children are growing up and your mortgage is largest. A 30-year term is ideal if you have young children or a new 30-year mortgage. A 10-year term works well if you\'re close to retirement or have a specific short-term debt to cover. Visit our <a href="/term-life" class="text-[#D97642] font-semibold hover:underline">term life insurance page</a> to compare options side by side.',
      },
      {
        id: 'term-no-exam',
        question: 'Can I get term life insurance without a medical exam?',
        answer:
          'Yes. Many carriers offer "accelerated underwriting" or "no-exam" term life policies for applicants under age 60 in good health. You answer a health questionnaire online, and most approvals happen within 24–48 hours. Coverage amounts up to $1,000,000 are often available without an exam. Applicants with certain health conditions may still need a traditional exam for the best rates. See all <a href="/no-medical-exam" class="text-[#D97642] font-semibold hover:underline">no-exam life insurance options →</a>',
      },
      {
        id: 'term-convert',
        question: 'Can I convert my term policy to permanent insurance later?',
        answer:
          'Most term life policies include a conversion option that allows you to convert to <a href="/whole-life" class="text-[#D97642] font-semibold hover:underline">whole life</a> or universal life insurance without a new medical exam — typically within the first 10–20 years of the policy. This is valuable if your health changes and you want permanent coverage later. Ask about conversion options when comparing policies.',
      },
      {
        id: 'term-expire',
        question: 'What happens when my term life policy expires?',
        answer:
          'When your term ends, coverage simply stops. You have several options: (1) Let it lapse if you no longer need coverage (e.g., your mortgage is paid off and children are grown). (2) Renew the policy annually at a higher rate. (3) Convert to a <a href="/whole-life" class="text-[#D97642] font-semibold hover:underline">permanent whole life policy</a> before the term ends. (4) Apply for a new term policy. Planning ahead before expiration gives you the most options. <a href="/book-appointment" class="text-[#D97642] font-semibold hover:underline">Talk to Benjamin about your options →</a>',
      },
    ],
  },
  {
    id: 'whole-life',
    label: 'Whole Life',
    icon: 'ri-infinity-line',
    faqs: [
      {
        id: 'whole-life-cash-value',
        question: 'How does whole life insurance cash value work?',
        answer:
          'A portion of every <a href="/whole-life" class="text-[#D97642] font-semibold hover:underline">whole life</a> premium goes into a cash value account that grows tax-deferred at a guaranteed rate (typically 2–4%). Over time, this cash value becomes a living asset you can borrow against, use to pay premiums, or surrender for cash. Unlike a 401(k), cash value growth is guaranteed and not subject to market volatility. Learn how this fits into a <a href="/generational-wealth" class="text-[#D97642] font-semibold hover:underline">generational wealth strategy →</a>',
      },
      {
        id: 'whole-life-worth-it',
        question: 'Is whole life insurance worth it in Indiana?',
        answer:
          '<a href="/whole-life" class="text-[#D97642] font-semibold hover:underline">Whole life insurance</a> is worth it for specific goals: (1) Permanent death benefit for estate planning or leaving a legacy. (2) Tax-deferred savings that complement your retirement accounts. (3) Business succession planning. (4) Covering final expenses for life. It\'s generally not the best choice if your primary goal is affordable income replacement — <a href="/term-life" class="text-[#D97642] font-semibold hover:underline">term life</a> is better for that. Read our <a href="/term-vs-whole-life" class="text-[#D97642] font-semibold hover:underline">full comparison guide →</a>',
      },
      {
        id: 'whole-life-premiums',
        question: 'Do whole life insurance premiums ever increase?',
        answer:
          'No. One of the key benefits of <a href="/whole-life" class="text-[#D97642] font-semibold hover:underline">whole life insurance</a> is that your premium is locked in the day you buy the policy and never increases — even as you age or your health changes. This makes it especially valuable to purchase when you\'re young and healthy.',
      },
      {
        id: 'whole-life-borrow',
        question: 'Can I borrow against my whole life insurance policy?',
        answer:
          'Yes. Once your <a href="/whole-life" class="text-[#D97642] font-semibold hover:underline">whole life policy</a> has accumulated sufficient cash value (typically after 2–3 years), you can take a policy loan at a low interest rate — usually 5–8%. Unlike a bank loan, there\'s no credit check, no approval process, and no required repayment schedule. However, unpaid loans plus interest will reduce your death benefit. It\'s a flexible financial tool, but should be used thoughtfully.',
      },
    ],
  },
  {
    id: 'final-expense',
    label: 'Final Expense',
    icon: 'ri-hand-heart-line',
    faqs: [
      {
        id: 'final-expense-what',
        question: 'What is final expense insurance?',
        answer:
          '<a href="/final-expense" class="text-[#D97642] font-semibold hover:underline">Final expense insurance</a> (also called burial insurance or funeral insurance) is a small whole life policy designed to cover end-of-life costs — funeral, burial, medical bills, and other final expenses. Policies typically range from $5,000 to $50,000. Premiums are locked for life, there\'s no medical exam required, and the death benefit is paid tax-free directly to your beneficiary.',
      },
      {
        id: 'final-expense-health',
        question: 'Can I get final expense insurance if I have health problems?',
        answer:
          'Yes — this is one of the biggest advantages of <a href="/final-expense" class="text-[#D97642] font-semibold hover:underline">final expense insurance</a>. Simplified issue policies require only a short health questionnaire (no exam) and accept many common conditions like diabetes, high blood pressure, COPD, and past heart issues. Guaranteed issue policies accept everyone with no health questions at all, regardless of medical history. Even if you\'ve been declined elsewhere, coverage is available. See also: <a href="/no-medical-exam" class="text-[#D97642] font-semibold hover:underline">no medical exam life insurance →</a>',
      },
      {
        id: 'final-expense-cost',
        question: 'How much does final expense insurance cost in Indianapolis?',
        answer:
          'A healthy 65-year-old woman in Indianapolis can get $15,000 of <a href="/final-expense" class="text-[#D97642] font-semibold hover:underline">final expense coverage</a> for approximately $41–$55/month. Rates depend on your age, gender, health, and coverage amount. The average Indiana funeral costs $9,000–$14,000, so a policy that costs less than $2/day can eliminate that entire burden from your family. <a href="/book-appointment" class="text-[#D97642] font-semibold hover:underline">Get a free quote today →</a>',
      },
      {
        id: 'final-expense-waiting',
        question: 'Is there a waiting period before the policy pays out?',
        answer:
          'Level benefit and simplified issue <a href="/final-expense" class="text-[#D97642] font-semibold hover:underline">final expense policies</a> typically pay the full death benefit from day one. Guaranteed issue policies usually have a 2-year graded benefit — if you pass away in the first two years from natural causes, your beneficiary receives a return of premiums plus interest (typically 10%). After 2 years, the full benefit is paid. Accidental death is usually covered in full from day one, even with guaranteed issue.',
      },
    ],
  },
  {
    id: 'mortgage-protection',
    label: 'Mortgage Protection',
    icon: 'ri-home-heart-line',
    faqs: [
      {
        id: 'mortgage-protection-what',
        question: 'What is mortgage protection insurance?',
        answer:
          '<a href="/mortgage-protection" class="text-[#D97642] font-semibold hover:underline">Mortgage protection insurance</a> is a life insurance policy specifically designed to pay off your mortgage if you pass away. Unlike PMI (private mortgage insurance, which protects the lender), mortgage protection insurance pays your beneficiary directly — they can use it to pay off the mortgage and keep the home. It ensures your family never loses their home due to your death.',
      },
      {
        id: 'mortgage-protection-vs-term',
        question: 'Is mortgage protection insurance different from term life insurance?',
        answer:
          'They\'re similar but have key differences. <a href="/mortgage-protection" class="text-[#D97642] font-semibold hover:underline">Mortgage protection insurance</a> is specifically designed to match your mortgage balance and term. Some policies have a decreasing death benefit that mirrors your declining mortgage balance. <a href="/term-life" class="text-[#D97642] font-semibold hover:underline">Term life insurance</a> has a fixed death benefit that your family can use for any purpose — not just the mortgage. Many financial advisors recommend a term life policy large enough to cover the mortgage plus other expenses. See our <a href="/term-vs-whole-life" class="text-[#D97642] font-semibold hover:underline">comparison guide →</a>',
      },
      {
        id: 'mortgage-protection-riders',
        question: 'What riders are available with mortgage protection insurance?',
        answer:
          'Common riders include: (1) Disability rider — makes your mortgage payments if you become disabled. (2) Critical illness rider — pays a lump sum if you\'re diagnosed with cancer, heart attack, or stroke. (3) Return of premium rider — refunds all premiums paid if you outlive the policy. (4) Unemployment rider — covers payments if you lose your job. These riders add cost but provide valuable additional protection. <a href="/mortgage-protection" class="text-[#D97642] font-semibold hover:underline">Explore mortgage protection options →</a>',
      },
    ],
  },
  {
    id: 'annuities',
    label: 'Annuities',
    icon: 'ri-line-chart-line',
    faqs: [
      {
        id: 'annuity-what',
        question: 'What is an annuity and how does it work?',
        answer:
          'An <a href="/annuities" class="text-[#D97642] font-semibold hover:underline">annuity</a> is a contract with an insurance company where you make a lump-sum payment or series of payments, and in return receive regular disbursements beginning either immediately or at some point in the future. Annuities are primarily used for retirement income — they guarantee you won\'t outlive your money. Fixed annuities offer a guaranteed interest rate; indexed annuities tie growth to a market index with downside protection.',
      },
      {
        id: 'annuity-indiana-tax',
        question: 'Are annuities tax-advantaged in Indiana?',
        answer:
          'Yes. <a href="/annuities" class="text-[#D97642] font-semibold hover:underline">Annuity</a> growth is tax-deferred — you don\'t pay taxes on earnings until you withdraw them, typically in retirement when you may be in a lower tax bracket. Indiana does not have an estate tax, which can make annuities an effective wealth transfer tool. Withdrawals before age 59½ may be subject to a 10% federal penalty plus ordinary income tax. See how annuities fit into a <a href="/generational-wealth" class="text-[#D97642] font-semibold hover:underline">generational wealth plan →</a>',
      },
      {
        id: 'annuity-vs-401k',
        question: 'How is an annuity different from a 401(k) or IRA?',
        answer:
          'A 401(k) and IRA have annual contribution limits; <a href="/annuities" class="text-[#D97642] font-semibold hover:underline">annuities</a> have no contribution limits, making them ideal for high earners who have maxed out other retirement accounts. Annuities offer guaranteed income for life — something a 401(k) cannot promise. However, annuities typically have higher fees and surrender charges if you withdraw early. They work best as a complement to, not a replacement for, your existing retirement accounts. <a href="/wealth-strategy-call" class="text-[#D97642] font-semibold hover:underline">Book a wealth strategy call →</a>',
      },
    ],
  },
  {
    id: 'no-exam',
    label: 'No-Exam Coverage',
    icon: 'ri-stethoscope-line',
    faqs: [
      {
        id: 'no-exam-qualify',
        question: 'Who qualifies for no-exam life insurance in Indiana?',
        answer:
          'Most healthy applicants under age 60 qualify for <a href="/no-medical-exam" class="text-[#D97642] font-semibold hover:underline">no-exam life insurance</a>. Carriers use data from prescription databases, MIB (Medical Information Bureau), and motor vehicle records to assess risk without a physical exam. Applicants with serious health conditions (recent cancer, heart surgery, etc.) may still need a traditional exam or may only qualify for guaranteed issue coverage.',
      },
      {
        id: 'no-exam-coverage-amount',
        question: 'How much no-exam life insurance can I get?',
        answer:
          'Coverage amounts vary by carrier and age. Many carriers offer up to $1,000,000 in <a href="/no-medical-exam" class="text-[#D97642] font-semibold hover:underline">no-exam term life</a> for healthy applicants under 50. For applicants ages 50–70, no-exam coverage up to $500,000 is common. <a href="/final-expense" class="text-[#D97642] font-semibold hover:underline">Final expense no-exam policies</a> typically range from $5,000 to $50,000. Guaranteed issue policies (no health questions at all) are usually capped at $25,000.',
      },
      {
        id: 'no-exam-approval-time',
        question: 'How fast is approval for no-exam life insurance?',
        answer:
          'Most <a href="/no-medical-exam" class="text-[#D97642] font-semibold hover:underline">no-exam applications</a> are approved within 24–48 hours. Some carriers offer instant approval decisions within minutes for healthy applicants. Guaranteed issue <a href="/final-expense" class="text-[#D97642] font-semibold hover:underline">final expense policies</a> are often approved same-day. Compare this to traditional underwriting, which can take 4–8 weeks and requires a medical exam, blood draw, and urine sample.',
      },
    ],
  },
  {
    id: 'claims',
    label: 'Claims & Policy',
    icon: 'ri-file-text-line',
    faqs: [
      {
        id: 'how-to-file-claim',
        question: 'How does a beneficiary file a life insurance claim in Indiana?',
        answer:
          'To file a claim: (1) Obtain certified copies of the death certificate (typically 5–10 copies). (2) Contact the insurance company directly or through your agent. (3) Complete the carrier\'s claim form. (4) Submit the death certificate and claim form. Most carriers pay claims within 30–60 days of receiving complete documentation. Indiana law requires carriers to pay interest on delayed claims. <a href="/contact" class="text-[#D97642] font-semibold hover:underline">Contact Benjamin for claims assistance →</a>',
      },
      {
        id: 'beneficiary-update',
        question: 'How do I update my beneficiary?',
        answer:
          'Contact your insurance carrier directly or work with your agent to complete a beneficiary change form. You can update beneficiaries at any time — it\'s free and takes about 10 minutes. It\'s important to review beneficiaries after major life events: marriage, divorce, birth of a child, or death of a named beneficiary. Outdated beneficiary designations are one of the most common life insurance mistakes. <a href="/book-appointment" class="text-[#D97642] font-semibold hover:underline">Schedule a policy review →</a>',
      },
      {
        id: 'policy-lapse',
        question: 'What happens if I miss a premium payment?',
        answer:
          'Most policies have a 30-day grace period — your coverage remains active even if you miss a payment, as long as you pay within 30 days. If you miss the grace period, your policy lapses (coverage ends). Many carriers allow reinstatement within 2–5 years if you pay back premiums and provide evidence of insurability. <a href="/whole-life" class="text-[#D97642] font-semibold hover:underline">Whole life policies</a> with cash value may use that value to cover missed premiums automatically.',
      },
      {
        id: 'contestability',
        question: 'What is the contestability period?',
        answer:
          'The contestability period is typically the first 2 years of a life insurance policy. During this time, the insurance company can investigate and potentially deny a claim if they find material misrepresentation on the application (e.g., you failed to disclose a known health condition). After 2 years, the policy is "incontestable" — the carrier cannot deny a claim based on application errors, except in cases of outright fraud. <a href="/resources" class="text-[#D97642] font-semibold hover:underline">Read more in our resources library →</a>',
      },
    ],
  },
];

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState<string>('general');
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const currentCategory = faqCategories.find(c => c.id === activeCategory) ?? faqCategories[0];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/faq#faqpage`,
        url: `${SITE_URL}/faq`,
        name: 'Life Insurance FAQ - Indiana',
        inLanguage: 'en-US',
        mainEntity: faqCategories.flatMap(category =>
          category.faqs.map(faq => ({
            '@type': 'Question',
            '@id': `${SITE_URL}/faq#${faq.id}`,
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
              author: {
                '@type': 'Organization',
                name: 'Hoosier Life Insurance',
                url: SITE_URL,
              },
            },
          }))
        ),
      },
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/faq#webpage`,
        url: `${SITE_URL}/faq`,
        name: 'Frequently Asked Questions | Hoosier Life Insurance',
        description:
          'Get answers to common questions about term life, whole life, final expense, mortgage protection, and annuities. Expert guidance for Indiana families.',
        inLanguage: 'en-US',
        datePublished: '2024-01-15T00:00:00-05:00',
        dateModified: new Date().toISOString(),
        isPartOf: {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: SITE_URL,
          name: 'Hoosier Life Insurance',
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, item: { '@id': SITE_URL, name: 'Home' } },
            { '@type': 'ListItem', position: 2, item: { '@id': `${SITE_URL}/faq`, name: 'FAQ' } },
          ],
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', '.faq-question'],
        },
      },
    ],
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = 'Life Insurance FAQ Indiana | Hoosier Life Insurance';
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta(
      'description',
      'Get answers to common questions about term life, whole life, final expense, mortgage protection, and annuities in Indiana. Expert guidance from a local Indianapolis agent.'
    );
    setMeta('keywords', 'life insurance FAQ Indiana, term life questions, whole life insurance FAQ, final expense insurance questions, Indianapolis life insurance');
    setMeta('og:title', 'Life Insurance FAQ Indiana | Hoosier Life Insurance', true);
    setMeta(
      'og:description',
      'Get answers to common questions about term life, whole life, final expense, mortgage protection, and annuities in Indiana.',
      true
    );
    setMeta('og:url', `${SITE_URL}/faq`, true);
    setMeta('og:type', 'website', true);
    setMeta('og:site_name', 'Hoosier Life Insurance', true);
    setMeta('og:image', 'https://hoosierlifeinsurance.com/og-image.jpg', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Life Insurance FAQ Indiana | Hoosier Life Insurance');
    setMeta('twitter:description', 'Get answers to common questions about life insurance in Indiana. Expert guidance from a local Indianapolis agent.');
    setMeta('twitter:image', 'https://hoosierlifeinsurance.com/og-image.jpg');
    setMeta('last-modified', new Date().toISOString());

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/faq`);

    // Inject FAQ schema into <head>
    const schemaId = 'schema-faq-page';
    let schemaEl = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!schemaEl) {
      schemaEl = document.createElement('script');
      schemaEl.id = schemaId;
      schemaEl.type = 'application/ld+json';
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify(faqSchema);

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-[#1A3A52] border-b border-[#1A3A52]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
            <i className="ri-home-4-line text-base"></i>
            <span>Home</span>
          </Link>
          <i className="ri-arrow-right-s-line text-white/40"></i>
          <span className="text-[#D97642] font-semibold">FAQ</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-28 pb-16 bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#D97642]/20 text-[#F4A97A] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <i className="ri-question-answer-line"></i>
            Life Insurance Questions Answered
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Clear, honest answers about life insurance in Indiana — from term life and whole life to final expense and annuities.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-white/70">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-[#D97642]"></i>
              <span>{faqCategories.reduce((acc, c) => acc + c.faqs.length, 0)} questions answered</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-map-pin-line text-[#D97642]"></i>
              <span>Indiana-specific guidance</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-user-heart-line text-[#D97642]"></i>
              <span>From a local Indianapolis agent</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* Sidebar — Category Tabs */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 lg:sticky lg:top-28">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 px-2">Categories</p>
                <nav className="flex flex-col gap-1">
                  {faqCategories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => { setActiveCategory(cat.id); setOpenFaq(null); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all text-left cursor-pointer ${
                        activeCategory === cat.id
                          ? 'bg-[#1A3A52] text-white shadow-md'
                          : 'text-[#4A5568] hover:bg-[#F9F7F4] hover:text-[#1A3A52]'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0">
                        <i className={`${cat.icon} text-base ${activeCategory === cat.id ? 'text-[#D97642]' : ''}`}></i>
                      </div>
                      <span>{cat.label}</span>
                      <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-bold ${
                        activeCategory === cat.id ? 'bg-[#D97642] text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {cat.faqs.length}
                      </span>
                    </button>
                  ))}
                </nav>

                {/* CTA */}
                <div className="mt-6 bg-[#D97642]/10 rounded-xl p-4 text-center">
                  <p className="text-sm font-semibold text-[#1A3A52] mb-1">Still have questions?</p>
                  <p className="text-xs text-[#4A5568] mb-3">Talk to Benjamin Wright directly.</p>
                  <a
                    href="tel:+13176482983"
                    className="block w-full py-2.5 bg-[#D97642] text-white text-sm font-bold rounded-lg hover:bg-[#C26638] transition-all text-center whitespace-nowrap"
                  >
                    Call (317) 648-2983
                  </a>
                </div>
              </div>
            </aside>

            {/* FAQ List */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1A3A52]">
                  <i className={`${currentCategory.icon} text-xl text-[#D97642]`}></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#1A3A52]">{currentCategory.label}</h2>
                  <p className="text-sm text-gray-500">{currentCategory.faqs.length} questions</p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {currentCategory.faqs.map((faq) => (
                  <div
                    key={faq.id}
                    id={faq.id}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-shadow hover:shadow-md"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                      className="w-full flex items-start justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors gap-4"
                    >
                      <h3 className="faq-question text-base font-bold text-[#1A3A52] leading-snug">
                        {faq.question}
                      </h3>
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#D97642]/10 flex-shrink-0 mt-0.5">
                        <i className={`${openFaq === faq.id ? 'ri-subtract-line' : 'ri-add-line'} text-[#D97642] text-base`}></i>
                      </div>
                    </button>
                    {openFaq === faq.id && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <p className="text-sm text-[#4A5568] leading-relaxed pt-4">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-10 bg-gradient-to-br from-[#0F2A3F] to-[#1A3A52] rounded-2xl p-8 text-white">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">Didn't find your answer?</h3>
                    <p className="text-white/75 text-sm leading-relaxed">
                      Benjamin Wright is a licensed Indianapolis agent with 12 years of experience. He'll answer any question — no pressure, no obligation.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                    <a
                      href="tel:+13176482983"
                      className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all text-sm whitespace-nowrap text-center"
                    >
                      Call (317) 648-2983
                    </a>
                    <Link
                      to="/book-appointment"
                      className="px-6 py-3 bg-white/15 border border-white/30 text-white rounded-lg font-semibold hover:bg-white/25 transition-all text-sm whitespace-nowrap text-center"
                    >
                      Book Free Consultation
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[#1A3A52] mb-3">Explore Coverage Options</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Ready to go beyond the FAQ? Learn more about each type of coverage we offer Indiana families.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                label: 'Term Life Insurance',
                href: '/term-life',
                icon: 'ri-calendar-check-line',
                desc: 'Affordable coverage for 10, 20, or 30 years. Best for income replacement during your working years.',
                cta: 'See Term Life Plans',
              },
              {
                label: 'Whole Life Insurance',
                href: '/whole-life',
                icon: 'ri-infinity-line',
                desc: 'Permanent coverage that builds cash value over time. Premiums never increase.',
                cta: 'Explore Whole Life',
              },
              {
                label: 'Final Expense Insurance',
                href: '/final-expense',
                icon: 'ri-hand-heart-line',
                desc: 'Cover funeral and burial costs. No medical exam required. Guaranteed acceptance available.',
                cta: 'Get Final Expense Quote',
              },
              {
                label: 'Mortgage Protection',
                href: '/mortgage-protection',
                icon: 'ri-home-heart-line',
                desc: 'Keep your family in their home. Pays off your mortgage if you pass away unexpectedly.',
                cta: 'Protect Your Home',
              },
              {
                label: 'Annuities',
                href: '/annuities',
                icon: 'ri-line-chart-line',
                desc: 'Guaranteed retirement income you can\'t outlive. Tax-deferred growth with no market risk.',
                cta: 'Learn About Annuities',
              },
              {
                label: 'No Medical Exam',
                href: '/no-medical-exam',
                icon: 'ri-stethoscope-line',
                desc: 'Get covered in 24–48 hours with no blood draw or physical exam required.',
                cta: 'Apply Without an Exam',
              },
            ].map(item => (
              <Link
                key={item.href}
                to={item.href}
                className="group flex flex-col gap-4 bg-[#F9F7F4] rounded-2xl p-6 border border-gray-100 hover:border-[#D97642] hover:bg-white hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1A3A52] group-hover:bg-[#D97642] transition-colors flex-shrink-0">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-base font-bold text-[#1A3A52] group-hover:text-[#D97642] transition-colors leading-tight">{item.label}</h3>
                </div>
                <p className="text-sm text-[#4A5568] leading-relaxed flex-1">{item.desc}</p>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-[#D97642] group-hover:gap-2.5 transition-all">
                  <span>{item.cta}</span>
                  <i className="ri-arrow-right-line text-base"></i>
                </div>
              </Link>
            ))}
          </div>

          {/* Secondary links row */}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {[
              { label: 'Term vs. Whole Life Guide', href: '/term-vs-whole-life' },
              { label: 'Generational Wealth', href: '/generational-wealth' },
              { label: 'Life Insurance Indiana', href: '/life-insurance-indiana' },
              { label: 'Life Insurance Indianapolis', href: '/life-insurance-indianapolis' },
              { label: 'Life Insurance Fort Wayne', href: '/life-insurance-fort-wayne' },
              { label: 'All Services', href: '/all-services' },
            ].map(link => (
              <Link
                key={link.href}
                to={link.href}
                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-all cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
